import type { ReactNode } from 'react'

export function LegalDocShell({
  eyebrow,
  title,
  lastUpdated,
  intro,
  children,
}: {
  eyebrow: string
  title: string
  lastUpdated: string
  intro: ReactNode
  children: ReactNode
}) {
  return (
    <div className="min-h-screen">
      <div className="relative overflow-hidden border-b border-[rgba(0,191,255,0.08)]">
        <div
          className="pointer-events-none absolute -top-32 left-1/2 h-72 w-[140%] -translate-x-1/2 opacity-50"
          style={{
            background: 'radial-gradient(ellipse 75% 55% at 50% 0%, rgba(0, 191, 255,0.22), transparent 72%)',
          }}
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage: `linear-gradient(rgba(0,191,255,0.04) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0,191,255,0.04) 1px, transparent 1px)`,
            backgroundSize: '48px 48px',
          }}
        />
        <div className="relative px-4 py-14 sm:px-6 sm:py-20">
          <div className="mx-auto flex max-w-3xl flex-col items-center text-center">
            <p
              className="mb-3 text-[11px] font-semibold uppercase tracking-[0.35em]"
              style={{ color: '#93c5fd' }}
            >
              {eyebrow}
            </p>
            <h1 className="mb-5 font-display text-4xl tracking-wide chrome-text sm:text-6xl md:text-7xl">
              {title}
            </h1>
            <div className="mb-8 flex flex-wrap items-center justify-center gap-2 text-sm" style={{ color: '#5a6d8a' }}>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#00bfff] shadow-[0_0_8px_rgba(0, 191, 255,0.8)]" />
                Last updated {lastUpdated}
              </span>
            </div>
            <div
              className="w-full rounded-2xl border border-white/[0.1] bg-gradient-to-br from-white/[0.06] to-white/[0.02] p-5 text-left shadow-xl shadow-black/40 sm:p-7"
              style={{ boxShadow: '0 0 0 1px rgba(0,191,255,0.08) inset, 0 25px 50px -12px rgba(0,0,0,0.5)' }}
            >
              <div className="text-sm leading-relaxed sm:text-base" style={{ color: '#8ecfff' }}>
                {intro}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16">{children}</div>
    </div>
  )
}

export function LegalSectionCard({
  title,
  badge,
  children,
  highlight,
}: {
  title: ReactNode
  badge?: ReactNode
  children: ReactNode
  highlight?: boolean
}) {
  return (
    <section
      className={`rounded-2xl border p-6 shadow-xl shadow-black/30 backdrop-blur-sm transition-[box-shadow] sm:p-8 ${
        highlight
          ? 'border-[rgba(0, 191, 255,0.22)] bg-[rgba(0, 191, 255,0.06)] hover:shadow-[0_0_48px_-16px_rgba(0, 191, 255,0.28)]'
          : 'border-white/[0.08] bg-gradient-to-br from-white/[0.045] via-transparent to-transparent hover:shadow-[0_0_40px_-14px_rgba(0, 191, 255,0.12)]'
      }`}
    >
      <h2 className="mb-5 flex flex-wrap items-start gap-3 font-display text-lg tracking-wide sm:text-xl">
        {badge ? (
          <span className="flex h-9 min-w-[2.25rem] shrink-0 items-center justify-center rounded-xl bg-[#00bfff]/15 text-sm font-bold tabular-nums text-[#a5d8ff] ring-1 ring-[#00bfff]/30">
            {badge}
          </span>
        ) : null}
        <span className="leading-snug" style={{ color: '#f0f6ff' }}>
          {title}
        </span>
      </h2>
      <div className="space-y-3 text-sm leading-relaxed sm:text-[15px]" style={{ color: '#8ecfff' }}>
        {children}
      </div>
    </section>
  )
}
